import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  TextInput,
  Button,
  FormGroup,
  RadioButtonGroup,
  RadioButton,
  Checkbox,
  Dropdown,
  Loading
} from 'carbon-components-react';
import { APP_URL, CFCProps, DraftChannelDataType } from 'types';
import { RootState } from 'redux/root.reducer';
import { selectDraftChannels, selectGmlProjectionItems } from 'redux/channels';
import { Launch16 } from '@carbon/icons-react';
import { setSandBoxTestingMode } from 'redux/lodgeEnquiry';
import { useHistory } from 'react-router-dom';

const EmailChannelForm: React.FC<Props | any> = (props: any) => {
  const params = new URLSearchParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const draftChannel: DraftChannelDataType = useSelector(selectDraftChannels);
  const gmlProjectionOptions: any = useSelector(selectGmlProjectionItems);
  const [
    gmlProjectionSelected,
    setGmlProjectionSelected
  ] = useState<Object | null>(null);
  const [isLoading, setIsLoading] = useState<boolean | undefined>();
  useEffect(() => {
    if (draftChannel) {
      setIsLoading(draftChannel.submitting);
    }
  }, [draftChannel]);

  const { advancedOptions, values, handleSubmit } = props;
  const gmlProjectionItems: [] =
    gmlProjectionOptions &&
    gmlProjectionOptions.map((item: any) => ({
      label: item.label,
      value: item.code
    }));
  const _isRequiredAttachment = !(
    values.isGifRequired ||
    values.isGmlRequired ||
    values.isXmlRequired ||
    values.isPdfRequired
  );

  useEffect(() => {
    if (values) {
      const { gmlProjection } = values;
      if (gmlProjection) {
        const gmlVal = gmlProjectionOptions?.filter(
          (item: any) => item.code === gmlProjection
        )[0];
        gmlVal && setGmlProjectionSelected(gmlVal);
      } else {
        setGmlProjectionSelected(null);
      }
    }
  }, [_isRequiredAttachment, values, gmlProjectionOptions]);

  const sandBoxTestHandler = () => {
    dispatch(setSandBoxTestingMode({ isSandboxTest: true }));
    params.append('isSandboxTest', 'true');
    history.push({
      pathname: APP_URL.LODGE_NEW_ENQUIRY,
      search: '?' + params.toString()
    });
  };

  return (
    <React.Fragment>
      <section className="form-section--item">
        <h4 className="section__sub-heading">
          The Legacy Fallback option has been enabled for you
        </h4>
        <p className="footer--content">
          Legacy Fallback is provided on request for members who have been
          unable to prepare for the referral service changes in time for go
          live. If you require changes to your configuration, these must be made
          via the BYDA helpdesk.
          <br />
          <br />
          Use the <i>Test run</i> button to perform test enquiries against your
          referral channel. Once you are happy that the configuration is correct
          click on <i>Make Active</i>.
        </p>
      </section>
      <hr className="section__divider" />
      <div className="form-mid--body">
        <div className="form-section--item">
          <TextInput
            id="emailNormalEnquiries"
            name="emailNormalEnquiries"
            labelText="Email Address"
            helperText="One or more comma-separated email addresses"
            defaultValue={values.referralSummaryEmail}
            data-testid="emailNormalEnquiries"
            disabled={true}
          />
        </div>
        <hr className="section__divider" />
        <FormGroup className="attachments" legendText="Require attachment">
          <div className="checkbox-fromgroup">
            <Checkbox
              labelText="GIF"
              value="GIF"
              defaultChecked={values.isGifRequired}
              id="isGifRequired"
              disabled={true}
            />
            <Checkbox
              labelText="GML"
              value="GML"
              defaultChecked={values.isGmlRequired}
              id="isGmlRequired"
              disabled={true}
            />
            <Checkbox
              labelText="XML"
              value="XML"
              defaultChecked={values.isXmlRequired}
              id="isXmlRequired"
              disabled={true}
            />
            <Checkbox
              labelText="PDF"
              value="PDF"
              defaultChecked={values.isPdfRequired}
              id="isPdfRequired"
              disabled={true}
            />
          </div>
        </FormGroup>
        {values.isGmlRequired && gmlProjectionItems?.length > 0 && (
          <>
            <FormGroup className="gml-version" legendText="GML version">
              <RadioButtonGroup
                defaultSelected={values.gmlVersion}
                name="gml-version-radio-button-group"
              >
                <RadioButton
                  id="gml-version-radio-1"
                  labelText="GML v2"
                  value="GMLv2"
                  disabled={true}
                />
                <RadioButton
                  id="gml-version-radio-2"
                  labelText="GML v3"
                  value="GMLv3"
                  disabled={true}
                />
              </RadioButtonGroup>
            </FormGroup>
            <div className="form-section--item">
              <Dropdown
                ariaLabel="Dropdown gml Projection"
                id="gmlProjection"
                items={gmlProjectionItems}
                label="Select..."
                titleText="GML Projection"
                initialSelectedItem={gmlProjectionSelected}
                selectedItem={gmlProjectionSelected}
                disabled={true}
              />
            </div>
          </>
        )}
      </div>
      <hr className="section__divider" />
      <div className="form-section--item">
        <TextInput
          id="referralSummaryEmail"
          name="referralSummaryEmail"
          labelText="Automatic email summary"
          helperText="A daily email of the previous day's referrak activity."
          defaultValue={
            advancedOptions.current.referralSummaryEmail || 'Not set'
          }
          disabled={true}
        />
      </div>
      <div className="form-section--item">
        <TextInput
          id="maxEnquiryArea"
          name="maxEnquiryArea"
          labelText="Maximum referral area (sq km)"
          helperText="An optional setting for large enquiry splitting"
          defaultValue={advancedOptions.current.maxEnquiryArea || 'Not set'}
          disabled={true}
        />
      </div>
      <hr className="section__divider" />
      <div className="form-button--wrapper">
        <Button
          renderIcon={Launch16}
          kind="tertiary"
          size="field"
          disabled={false}
          onClick={() => sandBoxTestHandler()}
        >
          Test run
        </Button>
        <Button
          className="submit-active"
          type="submit"
          kind={'primary'}
          size="field"
          data-testid="submit-active"
          onClick={() => {
            values.actionType = 'make-active';
            handleSubmit(values);
          }}
          disabled={false}
        >
          {'Make Active'}
        </Button>
        {isLoading && (
          <Loading
            small
            description="Active loading indicator"
            withOverlay={false}
          />
        )}
      </div>
      <p className="footer--content">
        Use ‘Test run’ to lodge a mock enquiry in Sandbox testing mode
      </p>
    </React.Fragment>
  );
};

const mapStateToProps = ({ referralChannels }: RootState) => ({
  referralChannels
});

const connector = connect(mapStateToProps);

type Props = CFCProps<typeof connector>;

EmailChannelForm.displayName = 'EmailChannelForm';

export default connector(EmailChannelForm);
